import { useQuery } from '@tanstack/react-query';
import { SubscriptionFeatures } from '@/lib/types';
import { fetchUserSubscription } from './useUserSubscriptions';
import { supabase } from '@/integrations/supabase/client';

// Default features when no subscription is active
const defaultFeatures: SubscriptionFeatures = {
  currency_conversion: false,
  multiple_currencies: false,
  price_comparison: false,
  grouped_contracts: false,
  contract_services: false,
  reminders: false,
  payment_type_selection: false,
  dashboard: false,
  contract_pdf_email: false,
  document_extraction: false,
  // Numeric limits
  max_contracts: 15,
  max_users: 1,
  max_erp_integrations: 0,
  monthly_doc_extractions: 0,
  storage_gb: 1,
  // Premium feature flags
  erp_integration: false,
  custom_fields: false,
  calendar_view: false,
  contract_notes: false,
  duplicate_detection: false,
  api_read: false,
  api_write: false,
  advanced_dashboard: false,
  // New custom plan features
  sales_analytics: false,
  cost_optimization: false,
  data_health: false,
  governing_documents: false,
};

// Map feature_key from catalog to SubscriptionFeatures key
const featureKeyToSubscriptionKey: Record<string, keyof SubscriptionFeatures> = {
  'contract_management': 'contract_services', // Core contract management
  'dashboard': 'dashboard',
  'reminders': 'reminders',
  'advanced_dashboard': 'advanced_dashboard',
  'sales_analytics': 'sales_analytics' as any,
  'cost_optimization': 'cost_optimization' as any,
  'erp_integration': 'erp_integration',
  'document_extraction': 'document_extraction',
  'custom_fields': 'custom_fields',
  'calendar_view': 'calendar_view',
  'contract_notes': 'contract_notes',
  'duplicate_detection': 'duplicate_detection',
  'api_read': 'api_read',
  'api_write': 'api_write',
  'data_health': 'data_health' as any,
  'governing_documents': 'governing_documents' as any,
};

// Build features from custom plan selection
// NOTE: Avoid depending on `feature_catalog` read-access for end users.
// Custom plans already store selected keys in `custom_features`.
const buildFeaturesFromCustomPlan = async (customFeatures: {
  feature_keys?: string[];
  enabled_features?: string[];
}): Promise<SubscriptionFeatures> => {
  const features: SubscriptionFeatures = { ...defaultFeatures };

  // Support both key names for backwards compatibility
  const selectedKeys = customFeatures.enabled_features || customFeatures.feature_keys || [];

  // Enable features based on selected keys
  for (const key of selectedKeys) {
    const mappedKey = featureKeyToSubscriptionKey[key];
    if (mappedKey && typeof features[mappedKey] === 'boolean') {
      (features as any)[mappedKey] = true;
    }

    // Special handling for some features
    if (key === 'contract_management') {
      features.contract_services = true;
      features.grouped_contracts = true;
      features.currency_conversion = true;
      features.multiple_currencies = true;
      features.price_comparison = true;
      features.payment_type_selection = true;
      features.contract_pdf_email = true;
    }

    if (key === 'erp_integration') {
      // Custom plans with ERP enabled should never be blocked at 0.
      features.max_erp_integrations = null; // Unlimited for custom plan with ERP
    }

    if (key === 'document_extraction') {
      features.monthly_doc_extractions = 10; // Base extractions for custom plan
    }
  }

  // Custom plans have unlimited contracts/users
  features.max_contracts = null;
  features.max_users = null;

  return features;
};

// Fetch subscription features for current user
export const fetchSubscriptionFeatures = async (): Promise<SubscriptionFeatures> => {
  try {
    console.log('Fetching subscription features...');
    
    // First check for active subscription
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      return defaultFeatures;
    }

    // Fetch subscription with custom features check
    // NOTE: We intentionally avoid joining `subscription_plans` here.
    // Some environments have ambiguous/blocked joins that cause this query to fail,
    // which would incorrectly fall back to default features (e.g. max_erp_integrations=0).
    const { data: subscriptionData, error: subscriptionError } = await supabase
      .from('user_subscriptions')
      .select('is_custom_plan, custom_features')
      .eq('user_id', userData.user.id)
      .eq('status', 'active')
      .maybeSingle();

    if (subscriptionError) {
      console.error('Error fetching subscription for features:', subscriptionError);
      throw subscriptionError;
    }

    if (!subscriptionData) {
      console.log('No active subscription found, returning default features');
      return defaultFeatures;
    }

    // Check if this is a custom plan
    if (subscriptionData.is_custom_plan && subscriptionData.custom_features) {
      console.log('Custom plan detected, building features from selection');
      return buildFeaturesFromCustomPlan(subscriptionData.custom_features as any);
    }

    // Standard plan - use existing logic
    const userSubscription = await fetchUserSubscription();
    
    if (!userSubscription || !userSubscription.plan) {
      console.log('No active subscription found, returning default features');
      return defaultFeatures;
    }
    
    // Merge plan features with numeric limits from plan
    const planFeatures = (userSubscription.plan.features || {}) as Partial<SubscriptionFeatures>;

    // ERP access is primarily governed by the numeric limit max_erp_integrations.
    const inferredErpIntegration =
      userSubscription.plan.max_erp_integrations === null
        ? true
        : (userSubscription.plan.max_erp_integrations ?? 0) > 0;

    // Helper to handle null (unlimited) vs undefined (use default)
    const getNumericLimit = <T>(planValue: T | null | undefined, defaultValue: T): T | null => {
      if (planValue === null) return null;
      if (planValue === undefined) return defaultValue;
      return planValue;
    };

    const mergedFeatures: SubscriptionFeatures = {
      ...defaultFeatures,
      ...planFeatures,
      erp_integration: planFeatures.erp_integration ?? inferredErpIntegration,
      max_contracts: getNumericLimit(userSubscription.plan.max_contracts, defaultFeatures.max_contracts),
      max_users: getNumericLimit(userSubscription.plan.max_users, defaultFeatures.max_users),
      max_erp_integrations: getNumericLimit(userSubscription.plan.max_erp_integrations, defaultFeatures.max_erp_integrations),
      monthly_doc_extractions: userSubscription.plan.monthly_doc_extractions ?? defaultFeatures.monthly_doc_extractions,
      storage_gb: userSubscription.plan.storage_gb ?? defaultFeatures.storage_gb,
    };
    
    console.log('Subscription features loaded:', mergedFeatures);
    return mergedFeatures;
  } catch (error) {
    console.error('Error fetching subscription features:', error);
    throw error;
  }
};

// React Query hook for subscription features
export const useSubscriptionFeaturesQuery = (options = {}) => 
  useQuery({
    queryKey: ['subscription-features'],
    queryFn: fetchSubscriptionFeatures,
    retry: 3,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...options
  });

export const useFeaturesHooks = () => {
  return {
    useSubscriptionFeaturesQuery
  };
};
